import React, { useRef, useState } from "react";
import { toast } from "react-toastify";
import "./ContactFAQ.css";
import { useLanguage } from "./LanguageContext";

const PHONE_PREFIXES = [
  { code: "+47", label: "+47 (NO)" },
  { code: "+39", label: "+39 (IT)" },
  { code: "+1", label: "+1 (US/CA)" },
  { code: "+44", label: "+44 (UK)" },
  { code: "+49", label: "+49 (DE)" },
  { code: "+33", label: "+33 (FR)" },
  { code: "+34", label: "+34 (ES)" },
  { code: "+41", label: "+41 (CH)" },
  { code: "+43", label: "+43 (AT)" },
  { code: "+31", label: "+31 (NL)" },
  { code: "+32", label: "+32 (BE)" },
  { code: "+46", label: "+46 (SE)" },
  { code: "+45", label: "+45 (DK)" },
  { code: "+358", label: "+358 (FI)" },
  { code: "+354", label: "+354 (IS)" },
  { code: "+351", label: "+351 (PT)" },
  { code: "+30", label: "+30 (GR)" },
  { code: "+353", label: "+353 (IE)" },
  { code: "+352", label: "+352 (LU)" },
  { code: "+356", label: "+356 (MT)" },
  { code: "+420", label: "+420 (CZ)" },
  { code: "+48", label: "+48 (PL)" },
  { code: "+40", label: "+40 (RO)" },
  { code: "+36", label: "+36 (HU)" },
  { code: "+385", label: "+385 (HR)" },
  { code: "+386", label: "+386 (SI)" },
  { code: "+359", label: "+359 (BG)" },
  { code: "+370", label: "+370 (LT)" },
  { code: "+371", label: "+371 (LV)" },
  { code: "+372", label: "+372 (EE)" },
  { code: "+380", label: "+380 (UA)" },
  { code: "+61", label: "+61 (AU)" },
  { code: "+64", label: "+64 (NZ)" },
  { code: "+81", label: "+81 (JP)" },
  { code: "+86", label: "+86 (CN)" },
  { code: "+91", label: "+91 (IN)" },
  { code: "+65", label: "+65 (SG)" },
  { code: "+82", label: "+82 (KR)" },
  { code: "+66", label: "+66 (TH)" },
  { code: "+62", label: "+62 (ID)" },
  { code: "+60", label: "+60 (MY)" },
  { code: "+55", label: "+55 (BR)" },
  { code: "+52", label: "+52 (MX)" },
  { code: "+54", label: "+54 (AR)" },
  { code: "+56", label: "+56 (CL)" },
  { code: "+57", label: "+57 (CO)" },
  { code: "+51", label: "+51 (PE)" },
  { code: "+506", label: "+506 (CR)" },
  { code: "+212", label: "+212 (MA)" },
  { code: "+216", label: "+216 (TN)" },
  { code: "+20", label: "+20 (EG)" },
  { code: "+27", label: "+27 (ZA)" },
  { code: "+971", label: "+971 (AE)" },
  { code: "+966", label: "+966 (SA)" },
  { code: "+972", label: "+972 (IL)" },
  { code: "+90", label: "+90 (TR)" }
];

const PrivacyModal = ({ isOpen, onClose }) => {
  const { t } = useLanguage();

  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="modal-content">
        <div className="modal-header">
          <h2>{t("privacyTitle")}</h2>
          <button className="close-btn" onClick={onClose}>
            ×
          </button>
        </div>
        <div className="modal-body">
          {t("privacyBody")
            .split("\n")
            .map((line, index) => (
              <p key={index}>{line.trim()}</p>
            ))}
        </div>
      </div>
    </div>
  );
};

const ValidationIcon = ({ value, isValid }) => {
  if (typeof value === "string" && value.trim() === "") return null;
  if (typeof value === "boolean" && !value) return null;

  return isValid ? (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#198754" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12"></polyline>
    </svg>
  ) : (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#dc3545" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18"></line>
      <line x1="6" y1="6" x2="18" y2="18"></line>
    </svg>
  );
};

const ContactFAQ = () => {
  const { t } = useLanguage();
  const form = useRef();
  const [isLoading, setIsLoading] = useState(false);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const [isPrivacyOpen, setIsPrivacyOpen] = useState(false);

  const [formValues, setFormValues] = useState({
    user_name: "",
    user_email: "",
    phone_number: "",
    message: ""
  });

  const handleChange = (e) => {
    setFormValues({
      ...formValues,
      [e.target.name]: e.target.value
    });
  };

  // Espressioni regolari per la validazione
  const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
  const phoneRegex = /^[\d\s()-]{5,20}$/;

  // Regole di validazione aggiornate
  const isNameValid = formValues.user_name.trim() !== "";
  const isEmailValid = emailRegex.test(formValues.user_email.trim());
  const isPhoneValid = phoneRegex.test(formValues.phone_number.trim());
  const isMessageValid = formValues.message.trim() !== "";

  const sendEmail = async (e) => {
    e.preventDefault();

    if (!isNameValid || !isEmailValid || !isMessageValid || !isPhoneValid) {
      toast.warning(t("contactWarning") || "Compila correttamente tutti i campi!");
      return;
    }

    if (!acceptedPrivacy) {
      toast.warning(t("contactPrivacyRequired") || "Devi accettare la privacy!");
      return;
    }

    setIsLoading(true);

    const formData = new FormData(form.current);
    
    const prefix = formData.get("phone_prefix");
    if (formValues.phone_number) {
      formData.append("phone", `${prefix} ${formValues.phone_number}`);
    }
    
    formData.delete("phone_prefix");
    formData.delete("phone_number");

    try {
      const response = await fetch("https://hook.eu1.make.com/utg3k59769yu2b1dtvavlirvqlqnynkm", {
        method: "POST",
        body: formData
      });

      if (response.ok) {
        toast.success(t("contactSuccess") || "Messaggio inviato!");
        
        setFormValues({ user_name: "", user_email: "", phone_number: "", message: "" });
        setAcceptedPrivacy(false);
        form.current.reset();
      } else {
        console.error("Errore di invio tramite webhook:", response.status);
        toast.error(t("contactError") || "Errore nell'invio del messaggio.");
      }
    } catch (error) {
      console.error("Errore di connessione:", error);
      toast.error(t("contactError") || "Errore nell'invio del messaggio.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section id="ContactFAQ" className="bg-light py-5">
      <PrivacyModal isOpen={isPrivacyOpen} onClose={() => setIsPrivacyOpen(false)} />

      <div className="container">
        <div className="row justify-content-center">
          <div className="col-12 d-flex justify-content-center">
            <div className="contact-form-wrapper w-100" style={{ maxWidth: "600px" }}>
              <h3 className="h5 fw-bold mb-3 text-center">{t("contactTitle")}</h3>
              <h3 className="h5 fw-bold mb-4 text-center">{t("contactSubtitle")}</h3>

              <form ref={form} onSubmit={sendEmail} noValidate>
                <div className="mb-3 position-relative">
                  <input
                    type="text"
                    name="user_name"
                    value={formValues.user_name}
                    onChange={handleChange}
                    className="form-control"
                    placeholder={`${t("contactNamePlaceholder")} *`}
                    style={{ paddingRight: "35px" }}
                    disabled={isLoading}
                  />
                  <span className="position-absolute" style={{ right: "10px", top: "50%", transform: "translateY(-50%)", zIndex: 5 }}>
                    <ValidationIcon value={formValues.user_name} isValid={isNameValid} />
                  </span>
                </div>

                <div className="mb-3 position-relative">
                  <input
                    type="email"
                    name="user_email"
                    value={formValues.user_email}
                    onChange={handleChange}
                    className="form-control"
                    placeholder={`${t("contactEmailPlaceholder")} *`}
                    style={{ paddingRight: "35px" }}
                    disabled={isLoading}
                  />
                  <span className="position-absolute" style={{ right: "10px", top: "50%", transform: "translateY(-50%)", zIndex: 5 }}>
                    <ValidationIcon value={formValues.user_email} isValid={isEmailValid} />
                  </span>
                </div>
                
                <div className="mb-3 input-group position-relative">
                  <select 
                    name="phone_prefix" 
                    className="form-select" 
                    style={{ maxWidth: "125px" }}
                    disabled={isLoading}
                    defaultValue="+47"
                  >
                    {PHONE_PREFIXES.map((item, index) => (
                      <option key={index} value={item.code}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                  <input
                    type="tel"
                    name="phone_number"
                    value={formValues.phone_number}
                    onChange={handleChange}
                    className="form-control"
                    placeholder={`${t("contactPhonePlaceholder")} *`}
                    style={{ paddingRight: "35px" }}
                    disabled={isLoading}
                  />
                  <span className="position-absolute" style={{ right: "10px", top: "50%", transform: "translateY(-50%)", zIndex: 5 }}>
                    <ValidationIcon value={formValues.phone_number} isValid={isPhoneValid} />
                  </span>
                </div>

                <div className="mb-3 position-relative">
                  <textarea
                    name="message"
                    value={formValues.message}
                    onChange={handleChange}
                    className="form-control"
                    rows="5"
                    placeholder={`${t("contactMessagePlaceholder")} *`}
                    style={{ paddingRight: "35px" }}
                    disabled={isLoading}
                  ></textarea>
                  <span className="position-absolute" style={{ right: "10px", top: "15px", zIndex: 5 }}>
                    <ValidationIcon value={formValues.message} isValid={isMessageValid} />
                  </span>
                </div>

                <div className="form-check mb-4 d-flex align-items-center">
                  <input
                    type="checkbox"
                    id="privacyFAQ"
                    className="form-check-input mt-0 me-2"
                    checked={acceptedPrivacy}
                    onChange={(e) => setAcceptedPrivacy(e.target.checked)}
                    disabled={isLoading}
                  />
                  <label htmlFor="privacyFAQ" className="form-check-label mb-0">
                    * {t("contactPrivacy")}{" "}
                    <span
                      className="text-primary"
                      style={{ cursor: "pointer", textDecoration: "underline" }}
                      onClick={() => setIsPrivacyOpen(true)}
                    >
                      ({t("contactPrivacyRead")})
                    </span>
                  </label>
                  <span className="ms-2 d-flex align-items-center">
                    <ValidationIcon value={acceptedPrivacy} isValid={acceptedPrivacy} />
                  </span>
                </div>

                <button
                  type="submit"
                  className="flash-btn px-4 w-100 mt-3"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <span
                      className="spinner-border spinner-border-sm"
                      role="status"
                      aria-hidden="true"
                    ></span>
                  ) : (
                    t("contactFAQButton")
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactFAQ;